import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import WorkspacePicker from './WorkspacePicker';
import FilmPicker from './FilmPicker';
import ScenePicker from './ScenePicker';
import ShotPicker from './ShotPicker';
import { extractDeepIds } from './urlHelpers';
import { cn } from '../../lib/utils';

/**
 * ContextSwitcher — the persistent top-left selector used on every
 * authenticated page.
 *
 *     [🏢 Acme Studios ▾]  /  [🎬 Movie A ▾]  [/ Scene 5 ▾]  [/ Shot 12 ▾]
 *
 * - Workspace + Film pickers are ALWAYS rendered (Film greys out without
 *   a workspace). This is the "persistent context" the user expects.
 * - Scene + Shot chips conditionally render based on whether their id
 *   appears in the URL — the chip components handle their own visibility.
 *
 * Phase 2: this component is rendered inside <TopBar />. It must not
 * change sidebar, badge wording or localStorage schema.
 */
export default function ContextSwitcher({ className }) {
  const location = useLocation();
  const { sceneId, shotId } = useMemo(
    () => extractDeepIds(location.pathname),
    [location.pathname]
  );

  return (
    <div
      className={cn(
        'flex items-center min-w-0 gap-0.5 md:gap-1',
        className
      )}
      data-testid="ctx-switcher"
      aria-label="Production context"
    >
      <WorkspacePicker />
      <ChevronRight
        className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0"
        aria-hidden
      />
      <FilmPicker />

      {sceneId && (
        <>
          <ChevronRight
            className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0"
            aria-hidden
          />
          <ScenePicker />
        </>
      )}
      {shotId && (
        <>
          <ChevronRight
            className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0"
            aria-hidden
          />
          <ShotPicker />
        </>
      )}
    </div>
  );
}
